import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import axios from "axios";

import LandingPage from "@/pages/LandingPage";
import Dashboard from "@/pages/Dashboard";
import Garage from "@/pages/Garage";
import Lobbies from "@/pages/Lobbies";
import LobbyRoom from "@/pages/LobbyRoom";
import Race from "@/pages/Race";
import Leaderboard from "@/pages/Leaderboard";
import Admin from "@/pages/Admin";
import BossGallery from "@/pages/BossGallery";
import Courses from "@/pages/Courses";
import CourseEditor from "@/pages/CourseEditor";
import CourseLanding from "@/pages/CourseLanding";
import Ideas from "@/pages/Ideas";
import InviteLanding from "@/pages/InviteLanding";
import LegalDocPage from "@/pages/LegalDocPage";
import CookieBanner from "@/components/CookieBanner";
import { AuthProvider, useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
axios.defaults.withCredentials = true;

// --- Custom-domain / deployment redirect ---------------------------------
// The bundle bakes REACT_APP_BACKEND_URL at build time. When the app is
// deployed to a CUSTOM DOMAIN (e.g. https://nitroautoheist.com), the baked
// URL (preview host) would cause every API call to be cross-origin, which
// silently breaks Emergent OAuth + authenticated requests. Since the
// Kubernetes ingress routes `/api/*` to the backend on whichever host is
// serving the frontend, we rewrite any outgoing axios request whose URL
// starts with the baked BACKEND_URL to use the current window.origin
// instead. This is a no-op on the preview host (same origin) and the fix
// on production custom domains.
if (typeof window !== "undefined" && BACKEND_URL) {
  axios.interceptors.request.use((config) => {
    if (
      window.location.origin !== BACKEND_URL &&
      typeof config.url === "string" &&
      config.url.startsWith(BACKEND_URL)
    ) {
      config.url = window.location.origin + config.url.slice(BACKEND_URL.length);
    }
    return config;
  });
}

function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("Connecting to the garage…");
  // useRef (not useState) to survive React StrictMode's double-invoke.
  // The Emergent playbook mandates this for one-shot session exchanges.
  const processed = React.useRef(false);
  React.useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? match[1] : null;
    if (!sessionId) {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const res = await axios.post(`${BACKEND_URL}/api/auth/session`, { session_id: sessionId });
        // CRITICAL: populate AuthContext BEFORE navigating, otherwise
        // ProtectedRoute sees user === null and bounces back to "/".
        setUser(res.data.user);
        window.history.replaceState(null, "", window.location.pathname);

        // If the user arrived via an /invite/<code> link, redeem now.
        // Fire-and-forget: a failure shouldn't block their dashboard entry.
        const pendingInvite = localStorage.getItem("nah_invite_code");
        if (pendingInvite) {
          localStorage.removeItem("nah_invite_code");
          axios
            .post(`${BACKEND_URL}/api/invites/accept`, { code: pendingInvite })
            .catch(() => null);
        }

        navigate("/dashboard", { replace: true, state: { user: res.data.user } });
      } catch (e) {
        setStatus("Authentication failed. Redirecting…");
        setTimeout(() => navigate("/", { replace: true }), 1500);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
      <div className="text-center">
        <div className="animate-pulse text-[10px] tracking-[0.4em] text-white/60 mb-3">EMERGENT OAUTH</div>
        <div className="font-heading text-4xl uppercase tracking-tighter">{status}</div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">
        <div className="text-[10px] tracking-[0.4em] text-white/60 animate-pulse">LOADING GARAGE…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect session_id in URL fragment synchronously before anything else
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/invite/:code" element={<InviteLanding />} />
      <Route path="/terms" element={<LegalDocPage kind="terms" />} />
      <Route path="/privacy" element={<LegalDocPage kind="privacy" />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/garage"
        element={
          <ProtectedRoute>
            <Garage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lobbies"
        element={
          <ProtectedRoute>
            <Lobbies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/lobbies/:lobbyId"
        element={
          <ProtectedRoute>
            <LobbyRoom />
          </ProtectedRoute>
        }
      />
      <Route
        path="/race/:lobbyId"
        element={
          <ProtectedRoute>
            <Race />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leaderboard"
        element={
          <ProtectedRoute>
            <Leaderboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ideas"
        element={
          <ProtectedRoute>
            <Ideas />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bosses"
        element={
          <ProtectedRoute>
            <BossGallery />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses"
        element={
          <ProtectedRoute>
            <Courses />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/edit/:courseId"
        element={
          <ProtectedRoute>
            <CourseEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/courses/play/:code"
        element={
          <ProtectedRoute>
            <CourseLanding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Admin />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#121212",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                fontFamily: "'IBM Plex Sans', sans-serif",
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
